import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, BarChart3, Ban, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { api, errMsg, fmtDate } from "@/lib/api";

const StudentForm = ({ open, onOpenChange, initial, onSaved, cats }) => {
  const [f, setF] = useState({ name: "", email: "", password: "", category: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF({ name: initial?.name || "", email: initial?.email || "", password: "", category: initial?.category || "" }); }, [open, initial]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { name: f.name, email: f.email, password: f.password || null, category: f.category || null };
      if (initial?.id) await api.put(`/admin/students/${initial.id}`, body); else await api.post("/admin/students", body);
      toast.success(initial?.id ? "Student updated" : "Student added");
      onOpenChange(false);
      onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="student-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{initial?.id ? "Edit student" : "Add student"}</DialogTitle>
          <DialogDescription>
            {initial?.id ? "Update this student's details or exam category." : "Students usually sign in with their pinslib.in account — add here only if you need a separate local account."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="space-y-1.5"><Label>Full name</Label><Input required value={f.name} onChange={set("name")} data-testid="admin-student-name-input" /></div>
          <div className="space-y-1.5"><Label>Email</Label><Input type="email" required value={f.email} onChange={set("email")} data-testid="admin-student-email-input" /></div>
          <div className="space-y-1.5">
            <Label>Exam category</Label>
            <Select value={f.category || "__none__"} onValueChange={(v) => setF({ ...f, category: v === "__none__" ? "" : v })}>
              <SelectTrigger data-testid="admin-student-category-select"><SelectValue placeholder="Not set" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Not set —</SelectItem>
                {cats.map((c) => <SelectItem key={c.id} value={c.name} data-testid={`admin-student-category-option-${c.name}`}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>{initial?.id ? "Local password (optional, overrides pinslib)" : "Local password (optional — leave blank to use pinslib)"}</Label>
            <Input type="text" minLength={6} value={f.password} onChange={set("password")} data-testid="admin-student-password-input" placeholder="Leave blank for pinslib login" />
          </div>
          <DialogFooter><Button type="submit" disabled={saving} data-testid="admin-save-student-button" className="bg-[#2563EB] hover:bg-[#1D4ED8] hover:text-white">Save student</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function AdminStudents() {
  const nav = useNavigate();
  const [students, setStudents] = useState([]);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = () => Promise.all([api.get("/admin/students"), api.get("/admin/categories")])
    .then(([s, c]) => { setStudents(s.data); setCats(c.data.filter((x) => x.status === "approved")); })
    .catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const remove = async (s) => {
    try { await api.delete(`/admin/students/${s.id}`); toast.success("Student deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const toggleSuspension = async (s) => {
    try {
      await api.post(`/admin/students/${s.id}/suspension`, { suspended: !s.is_suspended });
      toast.success(s.is_suspended ? `${s.name} reactivated` : `${s.name} suspended — read-only access`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const shown = students.filter((s) => `${s.name} ${s.email} ${s.category || ""}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><div className="small-label">Control Panel</div><h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">Students</h1></div>
        <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="admin-add-student-button" className="bg-[#2563EB] hover:bg-[#1D4ED8] hover:text-white">
          <Plus className="h-4 w-4 mr-1.5" />Add student
        </Button>
      </div>
      <p className="text-sm text-slate-500 mt-2 max-w-2xl">Students sign in with their <span className="font-medium text-slate-700">pinslib.in</span> account; they appear here automatically after their first login.</p>
      <div className="relative mt-8 max-w-sm">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email or category" className="pl-9 bg-white" data-testid="admin-student-search-input" />
      </div>
      <div className="mt-4 bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <Table data-testid="admin-students-table">
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Category</TableHead>
            <TableHead>Status</TableHead><TableHead>Attempts</TableHead><TableHead>Avg score</TableHead><TableHead>Added</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {shown.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-500">No students found.</TableCell></TableRow>}
            {shown.map((s) => (
              <TableRow key={s.id} data-testid={`admin-student-row-${s.id}`}>
                <TableCell className="font-medium">{s.name}</TableCell>
                <TableCell className="text-slate-500">{s.email}</TableCell>
                <TableCell>
                  {s.category
                    ? <Badge className="bg-[#EFF6FF] text-[#1D4ED8] hover:bg-[#EFF6FF] font-mono text-[10px] uppercase">{s.category}</Badge>
                    : <span className="text-xs text-slate-400">Not set</span>}
                </TableCell>
                <TableCell>
                  {s.is_suspended
                    ? <Badge className="bg-red-100 text-red-800 hover:bg-red-100" data-testid={`admin-student-status-${s.id}`}>Suspended</Badge>
                    : <Badge className="bg-green-100 text-green-800 hover:bg-green-100" data-testid={`admin-student-status-${s.id}`}>Active</Badge>}
                </TableCell>
                <TableCell className="font-mono">{s.attempts}</TableCell>
                <TableCell className="font-mono">{s.attempts ? `${s.avg_percentage}%` : "-"}</TableCell>
                <TableCell className="text-slate-500">{fmtDate(s.created_at)}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="icon" variant="ghost" title="View results" onClick={() => nav(`/admin/results?student=${s.id}`)} data-testid={`admin-student-results-${s.id}`}><BarChart3 className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => toggleSuspension(s)}
                    title={s.is_suspended ? "Reactivate account" : "Suspend account (read-only)"}
                    data-testid={`admin-toggle-suspension-${s.id}`}
                    className={s.is_suspended ? "text-green-700 hover:bg-green-50 hover:text-green-800" : "text-amber-700 hover:bg-amber-50 hover:text-amber-800"}>
                    {s.is_suspended ? <CheckCircle className="h-4 w-4" /> : <Ban className="h-4 w-4" />}
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => { setEditing(s); setOpen(true); }} data-testid={`admin-edit-student-${s.id}`}><Pencil className="h-4 w-4" /></Button>
                  <ConfirmDelete testId={`admin-delete-student-${s.id}`} title="Delete student?" description="This removes the student account and all their results."
                    onConfirm={() => remove(s)}
                    trigger={<Button size="icon" variant="ghost" className="text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`admin-delete-student-${s.id}`}><Trash2 className="h-4 w-4" /></Button>} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <StudentForm open={open} onOpenChange={setOpen} initial={editing} onSaved={load} cats={cats} />
    </div>
  );
}
